import { inject, InjectionToken, Provider } from '@angular/core';
import { ContextService } from '@maximizer/core/shared/data-access';
import {
  ChildTranslateServiceConfig,
  provideChildTranslateService,
  provideTranslateService,
  RootTranslateServiceConfig,
  TranslateLoader,
} from '@ngx-translate/core';
import {
  provideTranslateHttpLoader,
  TRANSLATE_HTTP_LOADER_CONFIG,
  TranslateHttpLoader,
  TranslateHttpLoaderResource,
  TranslateMultiHttpLoaderConfig,
} from '@ngx-translate/http-loader';

declare const LOAD_UID: string;

const defaultResource: TranslateHttpLoaderResource = {
  prefix: './i18n/',
  suffix: '.json',
};

const defaultLoaderConfig: TranslateMultiHttpLoaderConfig = {
  resources: [defaultResource],
  useHttpBackend: true,
  enforceLoading: false,
};

const getAppPath = (context: ContextService): string => {
  if (context.embedded) {
    return context.debug
      ? 'http://localhost:4200/i18n/'
      : `${context.website}/ClientScript/AngularElements/i18n/`;
  }
  return defaultResource.prefix;
};

const getIntegrationPath = (context: ContextService): string => {
  return context.integration
    ? `${context.integration.url}/i18n/`
    : defaultResource.prefix;
};

export const SHARED_I18N_BUNDLES = new InjectionToken<readonly string[]>(
  'SHARED_I18N_BUNDLES',
);

export const provideSharedI18nBundle = (bundle: string): Provider => ({
  provide: SHARED_I18N_BUNDLES,
  useValue: bundle,
  multi: true,
});

export const withSharedBundles = (
  app: TranslateHttpLoaderResource,
  bundles: readonly string[],
): TranslateHttpLoaderResource[] => [
  ...Array.from(new Set(bundles), (bundle) => ({
    ...app,
    prefix: `${app.prefix}${bundle}/`,
  })),
  app,
];

const getLoader = (prefix: (context: ContextService) => string): Provider[] => {
  return [
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useFactory: (): TranslateMultiHttpLoaderConfig => ({
        ...defaultLoaderConfig,
        resources: withSharedBundles(
          {
            prefix: prefix(inject(ContextService)),
            suffix: `.json?uid=${LOAD_UID}`,
          },
          inject(SHARED_I18N_BUNDLES, { optional: true }) ?? [],
        ),
      }),
    },
    { provide: TranslateLoader, useClass: TranslateHttpLoader },
  ];
};

export const applicationTranslateConfiguration: RootTranslateServiceConfig = {
  loader: getLoader(getAppPath),
  fallbackLang: 'en',
};

export const integrationTranslateConfiguration: RootTranslateServiceConfig = {
  loader: getLoader(getIntegrationPath),
  fallbackLang: 'en',
};

export const testingTranslateConfiguration: RootTranslateServiceConfig = {
  loader: provideTranslateHttpLoader(defaultLoaderConfig),
  fallbackLang: 'en',
};

export const provideTranslateServiceForApp = (): Provider[] =>
  provideTranslateService(applicationTranslateConfiguration);

export const provideTranslateServiceForIntegration = (): Provider[] =>
  provideTranslateService(integrationTranslateConfiguration);

export const provideTranslateServiceForTesting = (): Provider[] =>
  provideTranslateService(testingTranslateConfiguration);

export const provideTranslateServiceForChild = (
  childConfiguration: ChildTranslateServiceConfig,
): Provider[] => provideChildTranslateService(childConfiguration);
